import React from 'react';
import { CheckCircle, Copy, Image as ImageIcon } from 'lucide-react';

interface Variant {
  caption: string;
  hook: string;
  hashtags: string[];
  cta: string;
  imagePrompt?: string;
  imageUrl?: string | null;
}

interface VariantSelectorProps {
  variants: Variant[];
  onSelect: (variant: Variant) => void;
  selectedIndex?: number;
}

export const VariantSelector: React.FC<VariantSelectorProps> = ({ variants, onSelect, selectedIndex }) => {
  const [copied, setCopied] = React.useState<number | null>(null);

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
        Varianten wählen ({variants.length} Vorschläge)
      </h4>
      {variants.map((v, i) => (
        <div
          key={i}
          onClick={() => onSelect(v)}
          style={{
            padding: 'var(--space-4)', borderRadius: 12, cursor: 'pointer',
            border: selectedIndex === i
              ? '2px solid #8B5CF6'
              : '1px solid rgba(255,255,255,0.08)',
            background: selectedIndex === i
              ? 'rgba(139,92,246,0.08)'
              : 'rgba(255,255,255,0.03)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (selectedIndex !== i) (e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)');
          }}
          onMouseLeave={(e) => {
            if (selectedIndex !== i) (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)');
          }}
        >
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#8B5CF6' }}>
              Variante {i + 1}
            </span>
            <div className="flex items-center gap-2">
              {selectedIndex === i && <CheckCircle size={14} style={{ color: '#10B981' }} />}
              <button
                onClick={(e) => { e.stopPropagation(); handleCopy(v.caption, i); }}
                className="btn btn-secondary"
                style={{ padding: '2px 6px', fontSize: 10 }}
              >
                {copied === i ? <CheckCircle size={10} /> : <Copy size={10} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {/* Variant Image Thumbnail */}
            {v.imageUrl ? (
              <div style={{ flexShrink: 0 }}>
                <img
                  src={v.imageUrl}
                  alt={`Variante ${i + 1}`}
                  style={{
                    width: 80, height: 80, objectFit: 'cover',
                    borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                  }}
                />
              </div>
            ) : (
              <div style={{
                flexShrink: 0, width: 80, height: 80, borderRadius: 8,
                background: 'rgba(139,92,246,0.08)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                border: '1px dashed rgba(255,255,255,0.1)',
              }}>
                <ImageIcon size={20} style={{ color: 'rgba(139,92,246,0.4)' }} />
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              {v.hook && (
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text)',
                  marginBottom: 6, lineHeight: 1.3,
                }}>
                  {v.hook}
                </div>
              )}

              <div style={{
                fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5,
                maxHeight: 80, overflow: 'hidden',
                WebkitMaskImage: 'linear-gradient(180deg, #000 60%, transparent)',
              }}>
                {v.caption}
              </div>

              <div className="flex items-center gap-2" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                {v.hashtags.map((h, hi) => (
                  <span key={hi} style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 4,
                    background: 'rgba(59,130,246,0.1)', color: '#3B82F6',
                  }}>
                    {h}
                  </span>
                ))}
                {v.cta && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 4,
                    background: 'rgba(16,185,129,0.1)', color: '#10B981',
                  }}>
                    CTA: {v.cta}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
